<script lang="ts" setup>
  import { onMounted, useSlots } from 'vue'

  import { useVModel } from '@vueuse/core'

  const props = withDefaults(
    defineProps<{
      active?: boolean
      closeable?: boolean
      showShadow?: boolean

      sidebarClasses?: string
      sidebarHeaderClasses?: string
      sidebarBodyClasses?: string
      sidebarRightClasses?: string
      sidebarFooterClasses?: string
      closeBtnClasses?: string

      backdropClasses?: string
    }>(),
    {
      active: false,
      closeable: true,
      showShadow: true,

      sidebarClasses: 'w-[440px]',
      sidebarHeaderClasses: 'border-b border-mbrd relative py-6 px-8 h-20',
      sidebarBodyClasses: '',
      sidebarRightClasses: 'w-full',
      sidebarFooterClasses: 'py-4 px-8 h-[72px]',
      closeBtnClasses: 'top-5 right-5',

      backdropClasses: '',
    }
  )
  const emit = defineEmits<{ (e: 'update:active', value: boolean): void }>()
  const activeModel = useVModel(props, 'active', emit)

  onMounted(() => {
    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && props.closeable) close()
    })
  })

  const slots = useSlots()

  function close() {
    if (props.closeable) activeModel.value = false
  }
</script>

<template>
  <transition name="slide-fade">
    <!-- Window -->
    <div
      v-if="activeModel"
      class="sidebar window bg-white fixed top-0 left-0 max-w-[100%] h-full max-h-full z-40 flex"
      :class="[
        props.sidebarClasses,
        { 'sidebar--active': activeModel },
        { 'shadow-2xl': showShadow },
      ]"
    >
      <slot name="left" />

      <div class="h-full max-h-full" :class="props.sidebarRightClasses">
        <div
          v-if="slots.header"
          class="sidebar-header"
          :class="props.sidebarHeaderClasses"
        >
          <slot name="header" />

          <button
            v-if="props.closeable"
            aria-label="close"
            class="absolute cursor-pointer"
            :class="props.closeBtnClasses"
            @click.prevent="close"
          >
            <slot name="close-btn-icon">
              <svg
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 22L22 14M22 22L14 14"
                  stroke="#1F1F1F"
                  stroke-width="1.2"
                  stroke-linecap="square"
                />
                <rect
                  x="0.5"
                  y="0.5"
                  width="35"
                  height="35"
                  rx="17.5"
                  stroke="black"
                />
              </svg>
            </slot>
          </button>
        </div>

        <div class="sidebar-body" :class="props.sidebarBodyClasses">
          <slot />
        </div>

        <div
          v-if="slots.footer"
          class="sidebar-footer flex flex-wrap items-center"
          :class="props.sidebarFooterClasses"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </transition>

  <transition name="fade">
    <!-- Backdrop -->
    <div
      v-if="activeModel"
      class="bg-black/20 fixed top-0 left-0 w-full h-full z-30"
      :class="props.backdropClasses"
      @click.self="close"
    />
  </transition>
</template>

<style scoped>
  /* Transitions */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s linear;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.25s ease;
  }
  .slide-fade-enter-from,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateX(-100%);
  }

  .sidebar-body {
    height: calc(100% - 80px - 72px);
    max-height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }
</style>
